import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { HubClusters, HubFaq, HubHeader, hubItemList, type HubCluster } from "@/components/hub/ReadingHub";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { SITE } from "@/content/site";
import { LOCATIONS } from "@/content/locations";
import { faqSchema } from "@/lib/schema";

const FAQ = [
  { q: "Why is SEO in Bangladesh different from global SEO?", a: "Language, location, device and customer needs vary. Research your actual audience and Search Console queries rather than assuming a nationwide search-language percentage." },
  { q: "What's the highest-ROI SEO play for a Bangladeshi SME?", a: "Optimised Google Business Profile + 25–30 local citations + a single bilingual (Bangla + English) service page per city you serve. Together they unlock the map pack, which is where most local conversions happen in BD." },
  { q: "Should I publish in Bangla or English?", a: "Both, on separate URLs with hreflang. English ranks for buyer-research queries from professionals and diaspora; Bangla owns the informational long tail and mobile conversational queries that ChatGPT and Google increasingly route through." },
  { q: "Which cities have the most SEO opportunity in 2026?", a: "Dhaka and Chattogram dominate volume, but Tier-2 cities (Sylhet, Khulna, Rajshahi, Rangpur) have far lower competition and growing buyer intent — often a faster path to #1 than fighting for Dhaka head-terms." },
  { q: "Does AEO matter for a local Bangladesh business?", a: "Yes — ChatGPT and Perplexity already answer 'best dentist in Dhaka' style queries and cite local sources. Brands with proper local schema, Bangla content and citation networks get named; the rest get skipped." },
];

const CLUSTERS: HubCluster[] = [
  {
    title: "Local & GBP",
    description: "Map-pack rankings, Google Business Profile and the local citation network that powers them.",
    slugs: [
      "local-seo-rangpur-guide",
      "google-business-profile-optimization-checklist-2026",
      "local-citation-building-bangladesh-27-directories",
      "local-seo-service-area-businesses-no-storefront",
      "review-generation-google-filter-compliant",
    ],
  },
  {
    title: "Bangla-language SEO",
    description: "Bangla content, language implementation and AI-answer checks based on actual audience needs.",
    slugs: [
      "bangla-language-seo-bd-search-demand",
      "bengali-language-aeo-benchmark-2026",
      "bangladeshi-saas-invisible-in-chatgpt",
    ],
  },
  {
    title: "Tier-2 cities & multi-location",
    description: "Sylhet, Khulna, Rajshahi, Rangpur and beyond — lower competition, faster wins.",
    slugs: [
      "tier-2-bangladesh-seo-sylhet-khulna-rajshahi",
      "multi-location-seo-5-cities-no-cannibalization",
    ],
  },
  {
    title: "Industry plays in BD",
    description: "Vertical-specific SEO for the industries that move in Bangladesh.",
    slugs: [
      "seo-for-dhaka-saas-startups-2026",
      "chattogram-ecommerce-seo-guide-2026",
      "seo-healthcare-clinics-ymyl-bd",
      "seo-real-estate-portals-listing-schema",
      "seo-restaurants-menu-schema-local-pack",
    ],
  },
  {
    title: "Market context & measurement",
    description: "Bangladesh SEO context and practical measurement guides—not a verified nationwide benchmark.",
    slugs: [
      "state-of-seo-bangladesh-2026",
      "bangladesh-serp-volatility-q1-2026",
      "state-of-aeo-bangladesh-2026-data-report",
    ],
  },
];

export const Route = createFileRoute("/bangladesh-seo")({
  head: () =>
    buildSeo({
      title: "SEO in Bangladesh: Local Search, Bangla Content & Planning",
      description:
        "Find practical Bangladesh SEO guides for Google Business Profile, Bangla and English content, city-level planning and measuring organic enquiries.",
      path: "/bangladesh-seo",
      ogEyebrow: "Bangladesh SEO",
      image: "/og/bangladesh-seo.jpg",
      keywords:
        "Bangladesh SEO, BD SEO, local SEO Bangladesh, Bangla SEO, Dhaka SEO, Chattogram SEO, Sylhet SEO, Rangpur SEO",
      // No reciprocal Bangla counterpart: /bn's English pair is "/", not this hub.
    }),
  component: BdHub,
});

function BdHub() {
  const itemList = hubItemList("Bangladesh SEO Hub — Curated reading order", CLUSTERS);

  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE.url}/bangladesh-seo#collection`,
    name: "Bangladesh SEO Hub",
    url: `${SITE.url}/bangladesh-seo`,
    description:
      "Pillar hub for SEO in Bangladesh — local GBP, Bangla-language search, Tier-2 city plays, industry playbooks, and practical search measurement.",
    isPartOf: { "@id": `${SITE.url}/#website` },
    inLanguage: ["en", "bn"],
    about: [
      { "@type": "Country", name: "Bangladesh" },
      { "@type": "Thing", name: "Local SEO" },
      { "@type": "Thing", name: "Bangla-language SEO" },
    ],
  };

  const faqLd = faqSchema(FAQ);

  return (
    <>
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Bangladesh SEO Hub", path: "/bangladesh-seo" },
        ])}
      />
      <JsonLd data={collection} />
      <JsonLd data={itemList} />
      <JsonLd data={faqLd} />

      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Bangladesh SEO Hub", path: "/bangladesh-seo" },
        ]}
      />

      <HubHeader
        eyebrow="Bangladesh SEO Hub"
        title={<>SEO in Bangladesh, <span className="text-gradient">done properly.</span></>}
        intro={
          <>
            Bangladesh SEO is its own discipline — Bangla-first demand, mobile-heavy traffic,
            Google Business Profile quirks, and a citation network most international playbooks
            ignore. This hub is the end-to-end reading order I use with clients across Dhaka,
            Chattogram, Sylhet, Khulna and Rangpur. It covers local pack, Bangla content,
            city-level planning, industry programmes and practical measurement of your own search
            performance.
          </>
        }
        primary={{ to: "/seo-locations", label: <>See all {LOCATIONS.length} city pages</> }}
        secondary={{ to: "/bn", label: "বাংলা সংস্করণ" }}
      />

      <div className="container-x pb-24 space-y-16">
        <HubClusters clusters={CLUSTERS} />

        <section>
          <h2 className="font-display text-3xl md:text-4xl mb-3">Cities I serve</h2>
          <p className="text-muted-foreground max-w-2xl mb-6">
            Dedicated landing pages with local citations, GBP guidance and city-specific FAQ.
          </p>
          <ul className="flex flex-wrap gap-3">
            {LOCATIONS.map((l) => (
              <li key={l.slug}>
                <Link
                  to="/seo-services/$location"
                  params={{ location: l.slug }}
                  className="inline-flex rounded-full border border-border px-4 py-2 text-sm hover:border-primary/40 hover:text-primary transition"
                >
                  SEO in {l.city}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <HubFaq items={FAQ} />
      </div>
    </>
  );
}